<script lang="ts" module>
  // Opt out of default layout — changelog has its own simple layout
  export const layout = undefined;
</script>

<script lang="ts">
  import { page, router, Link } from '@inertiajs/svelte';
  import momentiumLogo from '../assets/momentium-logo.png';

  interface ChangelogEntry {
    version: string;
    date: string;
    title: string;
    body_html: string;
  }

  interface Props {
    entries: ChangelogEntry[];
  }

  let { entries }: Props = $props();

  let auth = $derived(
    $page.props.auth as { user: { id: number; email_address: string } | null } | undefined,
  );
  let currentUser = $derived(auth?.user);

  function formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
</script>

<div class="changelog-wrapper">
  <header class="changelog-header">
    <div class="header-left">
      <Link href="/" class="logo-link">
        <img src={momentiumLogo} alt="Momentium" class="logo" />
        <span class="logo-text">Momentium</span>
      </Link>
      <span class="breadcrumb">/ Changelog</span>
    </div>
    <div class="header-right">
      {#if currentUser}
        <button class="back-btn" onclick={() => router.visit('/')}>← Back to app</button>
      {:else}
        <Link href="/login" class="back-btn">Log in</Link>
      {/if}
    </div>
  </header>

  <main class="changelog-page">
    <div class="changelog-container">
      <div class="changelog-intro">
        <h1 class="changelog-title">Changelog</h1>
        <p class="changelog-subtitle">New features, improvements, and fixes for Momentium.</p>
      </div>

      {#if entries.length === 0}
        <div class="empty-state">
          <p>No releases yet. Stay tuned!</p>
        </div>
      {:else}
        <div class="entries">
          {#each entries as entry}
            <article class="entry" id={`v${entry.version}`}>
              <div class="entry-meta">
                <time class="entry-date">{formatDate(entry.date)}</time>
                <span class="entry-version">v{entry.version}</span>
              </div>
              <h2 class="entry-title">{entry.title}</h2>
              <div class="entry-body">
                <!-- eslint-disable-next-line svelte/no-at-html-tags -- Server-sanitized markdown -->
                {@html entry.body_html}
              </div>
            </article>
          {/each}
        </div>
      {/if}
    </div>
  </main>
</div>

<style>
  .changelog-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: #fafafa;
  }

  .changelog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :global(.logo-link) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-shrink: 0;
    text-decoration: none;
  }

  .logo {
    height: 1.5rem;
    flex-shrink: 0;
  }

  .logo-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
  }

  .breadcrumb {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .back-btn {
    font-size: 0.8125rem;
    color: #6b7280;
    background: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    padding: 0.25rem 0.75rem;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.15s;
  }

  .back-btn:hover {
    color: #374151;
    border-color: #d1d5db;
    background: #f9fafb;
  }

  @media (max-width: 767px) {
    .back-btn,
    :global(.logo-link) {
      display: inline-flex;
      align-items: center;
      min-height: var(--mobile-tap);
    }

    .back-btn {
      padding-inline: 0.75rem;
    }
  }

  .changelog-page {
    padding: 2rem 1rem;
    max-width: 680px;
    margin: 0 auto;
    width: 100%;
  }

  .changelog-intro {
    margin-bottom: 2.5rem;
  }

  .changelog-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.375rem;
  }

  .changelog-subtitle {
    font-size: 0.9375rem;
    color: #6b7280;
  }

  .empty-state {
    text-align: center;
    padding: 3rem 1rem;
    color: #9ca3af;
    font-size: 0.9375rem;
  }

  .entries {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .entry {
    padding: 1.75rem 0;
    border-top: 1px solid #e5e7eb;
  }

  .entry:first-child {
    border-top: none;
    padding-top: 0;
  }

  .entry-meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .entry-date {
    font-size: 0.8125rem;
    color: #6b7280;
    font-weight: 500;
  }

  .entry-version {
    font-size: 0.75rem;
    color: #9ca3af;
    background: #f3f4f6;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace;
  }

  .entry-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.75rem;
  }

  .entry-body {
    font-size: 0.9375rem;
    color: #374151;
    line-height: 1.7;
  }

  /* Markdown content styling */
  .entry-body :global(h2) {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
    margin-top: 1rem;
    margin-bottom: 0.375rem;
  }

  .entry-body :global(h2:first-child) {
    margin-top: 0;
  }

  .entry-body :global(ul) {
    margin: 0.25rem 0 0.75rem 0;
    padding-left: 1.25rem;
    list-style-type: disc;
  }

  .entry-body :global(ol) {
    margin: 0.25rem 0 0.75rem 0;
    padding-left: 1.25rem;
    list-style-type: decimal;
  }

  .entry-body :global(li) {
    margin-bottom: 0.25rem;
    color: #4b5563;
  }

  .entry-body :global(p) {
    margin-bottom: 0.5rem;
  }

  .entry-body :global(code) {
    font-size: 0.8125rem;
    background: #f3f4f6;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace;
  }

  .entry-body :global(strong) {
    font-weight: 600;
    color: #111827;
  }
</style>
